import { useEffect, useCallback, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoCloseOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoDownloadOutline,
  IoAddOutline,
  IoRemoveOutline,
} from "react-icons/io5";

type LightboxImage = {
  url: string;
  alt?: string;
};

interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export const ImageLightbox = ({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
}: ImageLightboxProps) => {
  // Use key-based reset: parent should change initialIndex before opening
  return (
    <AnimatePresence>
      {isOpen && (
        <LightboxContent
          key={`${initialIndex}-${images.length}`}
          images={images}
          initialIndex={initialIndex}
          onClose={onClose}
        />
      )}
    </AnimatePresence>
  );
};

const LightboxContent = ({
  images,
  initialIndex,
  onClose,
}: {
  images: LightboxImage[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTapRef = useRef(0);

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const navigateTo = (index: number) => {
    setCurrentIndex(index);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsImageLoaded(false);
  };

  const goNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      navigateTo(currentIndex + 1);
    }
  }, [currentIndex, images.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      navigateTo(currentIndex - 1);
    }
  }, [currentIndex]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const next = Math.max(prev - 0.5, 1);
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const handleDownload = async () => {
    const image = images[currentIndex];
    if (!image) return;

    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = image.alt || `imagem-${currentIndex + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab
      window.open(image.url, "_blank");
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowRight":
          goNext();
          break;
        case "ArrowLeft":
          goPrev();
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Drag to pan when zoomed
  const handlePointerDown = (e: React.PointerEvent) => {
    if (scale <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || scale <= 1) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  // Double tap to zoom
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (scale > 1) {
        resetZoom();
      } else {
        setScale(2);
      }
    }
    lastTapRef.current = now;
  };

  // Click backdrop to close (only when not zoomed and not dragging)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === containerRef.current && scale <= 1) {
      onClose();
    }
  };

  if (!images.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
    >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 text-white z-10">
            <span className="text-sm font-medium">
              {currentIndex + 1} de {images.length}
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 1}
                className="p-2 rounded-full hover:bg-white/20 transition-colors disabled:opacity-30"
                aria-label="Diminuir zoom"
              >
                <IoRemoveOutline size={22} />
              </button>
              <span className="text-xs w-12 text-center">{Math.round(scale * 100)}%</span>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 4}
                className="p-2 rounded-full hover:bg-white/20 transition-colors disabled:opacity-30"
                aria-label="Aumentar zoom"
              >
                <IoAddOutline size={22} />
              </button>
              <button
                onClick={handleDownload}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Download"
              >
                <IoDownloadOutline size={22} />
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Fechar"
              >
                <IoCloseOutline size={26} />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div
            ref={containerRef}
            className="flex-1 flex items-center justify-center relative overflow-hidden select-none"
            onClick={handleBackdropClick}
          >
            {/* Previous button */}
            {currentIndex > 0 && (
              <button
                onClick={goPrev}
                className="absolute left-2 md:left-4 z-10 p-2 md:p-3 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
                aria-label="Imagem anterior"
              >
                <IoChevronBackOutline size={24} />
              </button>
            )}

            {/* Image */}
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="max-w-full max-h-full flex items-center justify-center"
              style={{
                cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onClick={handleDoubleTap}
            >
              {!isImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <img
                src={images[currentIndex].url}
                alt={images[currentIndex].alt || `Imagem ${currentIndex + 1}`}
                className="max-w-[90vw] max-h-[80vh] object-contain transition-transform duration-150"
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                }}
                draggable={false}
                onLoad={() => setIsImageLoaded(true)}
              />
            </motion.div>

            {/* Next button */}
            {currentIndex < images.length - 1 && (
              <button
                onClick={goNext}
                className="absolute right-2 md:right-4 z-10 p-2 md:p-3 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
                aria-label="Próxima imagem"
              >
                <IoChevronForwardOutline size={24} />
              </button>
            )}
          </div>

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="flex justify-center gap-2 px-4 py-3 overflow-x-auto">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => navigateTo(idx)}
                  className={`w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                    idx === currentIndex
                      ? "border-white opacity-100"
                      : "border-transparent opacity-50 hover:opacity-80"
                  }`}
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </motion.div>
  );
};
